import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PlayCircle, Play, Youtube, Instagram, Video as VideoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Container, SectionHeader } from "@/components/Section";
import { api, resolveImg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

// Genuine Instagram Reel screenshots used as Reel covers (one per Reel, in order).
const REEL_COVERS = [
  "ptg/uploads/58b21dfc-57f6-4dba-80cb-61f6e1d22d9c.webp",
  "ptg/uploads/55525a49-92f1-4dd7-b54b-1c768cc3f2ca.webp",
  "ptg/uploads/a5b0a17f-7bb3-43da-9860-cc9dc213ccf9.webp",
  "ptg/uploads/ba95393f-a4be-4aff-a303-f0cf818f83ae.webp",
];

// Concise, accurate display titles for the genuine YouTube videos (links unchanged).
const VIDEO_TITLES = {
  dkm_QpJ4fZE: "Bad Debts, Provisions & Adjustments",
  "pMVI-OJhGkg": "Sacrifice Ratio, New Ratio & Admission of Partner",
  sVimozRqVbQ: "12th Economics: Utility",
  "80-mseDs9aI": "10th Geometry 1.4 (1)",
};

export default function Videos() {
  const { site } = useSite();
  const [videos, setVideos] = useState([]);
  const [reels, setReels] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/reels").then((r) => setReels(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="videos-page">
      <Seo title="Videos" description="Watch PTG lecture highlights and Reels. Follow PTG on YouTube and Instagram." />
      <section className="bg-blue-950 py-16 sm:py-20 text-white">
        <Container>
          <SectionHeader center={false} light eyebrow="YouTube & Instagram" title="Watch & follow PTG"
            subtitle="Selected lecture highlights and Reels from PTG." />
          <div className="flex flex-wrap gap-4">
            <a href={site.youtube_url || "#"} target={site.youtube_url ? "_blank" : "_self"} rel="noreferrer">
              <Button className="bg-red-600 hover:bg-red-700 h-11" data-testid="videos-youtube-btn"><Youtube className="mr-2 h-5 w-5" /> YouTube Channel</Button>
            </a>
            <a href={site.instagram_url || "#"} target={site.instagram_url ? "_blank" : "_self"} rel="noreferrer">
              <Button className="bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 h-11" data-testid="videos-instagram-btn"><Instagram className="mr-2 h-5 w-5" /> Instagram</Button>
            </a>
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-24">
        <Container>
          {videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.06}>
                  <button onClick={() => setActive(v)} className="block w-full text-left group" data-testid={`video-card-${i}`}>
                    <div className="relative overflow-hidden rounded-2xl">
                      <img src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center">
                        <PlayCircle className="h-16 w-16 text-white/90 group-hover:scale-110 transition-transform" />
                      </div>
                    </div>
                    <h4 className="mt-3 text-sm sm:text-base font-semibold text-slate-800 leading-snug line-clamp-2">{VIDEO_TITLES[v.youtube_id] || v.title}</h4>
                  </button>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center text-slate-500 flex flex-col items-center gap-3">
              <VideoIcon className="h-10 w-10 text-slate-300" />
              <p>New PTG lecture videos will be shared here soon.</p>
            </div>
          )}
        </Container>
      </section>

      {reels.length > 0 && (
        <section className="pb-16 sm:pb-24" data-testid="reels-section">
          <Container>
            <SectionHeader eyebrow="Instagram" title="PTG on Instagram"
              subtitle="Latest Reels from @prasadtutorgroup. Tap any reel to watch it on Instagram." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
              {reels.map((r, i) => (
                <Reveal key={r.id} delay={(i % 4) * 0.06}>
                  <a href={r.url} target="_blank" rel="noreferrer" data-testid={`reel-card-${i}`} className="group block">
                    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 bg-slate-900">
                      <img src={resolveImg(REEL_COVERS[i % REEL_COVERS.length])} alt="PTG Instagram Reel" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-slate-950/25 group-hover:from-slate-950/60 transition-colors" />
                      <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 text-white drop-shadow">
                        <Instagram className="h-4 w-4" /><span className="text-xs font-bold">Reels</span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/25 backdrop-blur-sm ring-1 ring-white/50 group-hover:scale-110 transition-transform duration-300">
                          <Play className="h-6 w-6 fill-white text-white" />
                        </div>
                      </div>
                      <p className="absolute bottom-2.5 left-0 right-0 text-center text-white text-xs font-semibold drop-shadow">Watch on Instagram</p>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
            <div className="mt-10 text-center">
              <a href={site.instagram_url || "#"} target={site.instagram_url ? "_blank" : "_self"} rel="noreferrer" data-testid="reels-follow-btn">
                <Button className="bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 h-12 px-8 font-bold"><Instagram className="mr-2 h-5 w-5" /> Follow PTG on Instagram</Button>
              </a>
            </div>
          </Container>
        </section>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl p-2 sm:p-4" data-testid="video-player-modal">
          {active && (
            <div>
              <div className="aspect-video w-full overflow-hidden rounded-lg">
                <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${active.youtube_id}`} title={active.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              <DialogTitle className="mt-3 text-base font-semibold text-slate-800 leading-snug">{VIDEO_TITLES[active.youtube_id] || active.title}</DialogTitle>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
